/**
 * Badge Collection Showcase Page
 * Displays all available badges, earned badges, and progress toward unearned badges
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    XPBar, 
    StreakIndicator, 
    BadgeDisplay,
    AnimatedScore 
} from '../components/gamification';
import {
    Trophy, Award, Star, Flame, Target, Zap, Crown, Gift,
    Medal, Sparkles, BookOpen, Mic, Video, Briefcase, Users,
    MessageCircle, Lock, CheckCircle2, TrendingUp, Loader2
} from 'lucide-react';

// Icon mapping for badges
const BADGE_ICONS = {
    'message-circle': MessageCircle,
    'repeat': TrendingUp,
    'award': Award,
    'star': Star,
    'briefcase': Briefcase,
    'users': Users,
    'handshake': Medal,
    'crown': Crown,
    'trophy': Trophy,
    'flame': Flame,
    'zap': Zap,
    'target': Target,
    'book': BookOpen,
    'book-open': BookOpen,
    'mic': Mic,
    'mic-2': Mic,
    'video': Video,
    'sparkles': Sparkles,
    'gift': Gift,
    'check': CheckCircle2,
    'check-circle': CheckCircle2,
    'play-circle': Target,
    'graduation-cap': Award,
    'code': Zap,
    'heart-pulse': Star,
    'dollar-sign': Crown,
    'sunrise': Star,
    'moon': Star,
    'compass': Target
};

// Tier styling
const TIER_STYLES = {
    bronze: { 
        bg: 'bg-amber-600/20', 
        border: 'border-amber-600', 
        text: 'text-amber-600',
        gradient: 'from-amber-600 to-amber-800'
    },
    silver: { 
        bg: 'bg-slate-400/20', 
        border: 'border-slate-400', 
        text: 'text-slate-400',
        gradient: 'from-slate-400 to-slate-600'
    },
    gold: { 
        bg: 'bg-yellow-500/20', 
        border: 'border-yellow-500', 
        text: 'text-yellow-500',
        gradient: 'from-yellow-500 to-yellow-700'
    },
    platinum: { 
        bg: 'bg-cyan-400/20', 
        border: 'border-cyan-400', 
        text: 'text-cyan-400',
        gradient: 'from-cyan-400 to-cyan-600'
    },
    diamond: { 
        bg: 'bg-violet-500/20', 
        border: 'border-violet-500', 
        text: 'text-violet-500',
        gradient: 'from-violet-500 to-violet-700'
    }
};

// Category configuration
const CATEGORY_CONFIG = {
    roleplay: { label: 'Role-Play', icon: MessageCircle, color: 'text-blue-500' },
    learning: { label: 'Learning', icon: BookOpen, color: 'text-emerald-500' },
    simulation: { label: 'Simulation', icon: Target, color: 'text-purple-500' },
    streak: { label: 'Streaks', icon: Flame, color: 'text-orange-500' },
    vertical: { label: 'Industry', icon: Briefcase, color: 'text-cyan-500' },
    voice: { label: 'Voice', icon: Mic, color: 'text-rose-500' },
    special: { label: 'Special', icon: Sparkles, color: 'text-yellow-500' }
};

const BadgeCollection = () => {
    const { api } = useAuth();
    const [loading, setLoading] = useState(true);
    const [badgeData, setBadgeData] = useState(null);
    const [profileData, setProfileData] = useState(null);
    const [activeTab, setActiveTab] = useState('all');
    const [selectedBadge, setSelectedBadge] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [badgesRes, profileRes] = await Promise.all([
                api.get('/gamification/badges'),
                api.get('/gamification/profile')
            ]);
            setBadgeData(badgesRes.data);
            setProfileData(profileRes.data);
        } catch (error) {
            console.error('Failed to fetch badge data:', error);
            toast.error('Failed to load badges');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getFilteredBadges = () => {
        if (!badgeData?.badges) return [];
        
        if (activeTab === 'all') return badgeData.badges;
        if (activeTab === 'earned') return badgeData.badges.filter(b => b.earned);
        if (activeTab === 'locked') return badgeData.badges.filter(b => !b.earned);
        
        // Filter by category
        return badgeData.badges.filter(b => b.category === activeTab);
    };

    const renderBadgeCard = (badge) => {
        const Icon = BADGE_ICONS[badge.icon] || Award;
        const tierStyle = TIER_STYLES[badge.tier] || TIER_STYLES.bronze;
        
        return (
            <motion.div
                key={badge.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                className={`
                    relative p-5 rounded-xl cursor-pointer transition-all
                    ${badge.earned 
                        ? `${tierStyle.bg} border-2 ${tierStyle.border} shadow-lg hover:shadow-xl` 
                        : 'bg-muted/30 border border-border opacity-60 grayscale hover:opacity-80 hover:grayscale-0'
                    }
                `}
                onClick={() => setSelectedBadge(badge)}
                data-testid={`badge-${badge.id}`}
            >
                {/* Earned indicator */}
                {badge.earned && (
                    <div className="absolute -top-2 -right-2">
                        <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${tierStyle.gradient} flex items-center justify-center`}>
                            <CheckCircle2 className="w-4 h-4 text-white" />
                        </div>
                    </div>
                )}
                
                {/* Lock indicator for unearned */}
                {!badge.earned && (
                    <div className="absolute top-2 right-2">
                        <Lock className="w-4 h-4 text-muted-foreground" />
                    </div>
                )}
                
                <div className="text-center space-y-3">
                    {/* Icon */}
                    <div className={`
                        w-16 h-16 mx-auto rounded-full flex items-center justify-center
                        ${badge.earned 
                            ? `bg-gradient-to-br ${tierStyle.gradient}` 
                            : 'bg-muted'
                        }
                    `}>
                        <Icon className={`w-8 h-8 ${badge.earned ? 'text-white' : 'text-muted-foreground'}`} />
                    </div>
                    
                    {/* Name */}
                    <h3 className={`font-semibold ${badge.earned ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {badge.name}
                    </h3>
                    
                    {/* Tier & Points */}
                    <div className="flex items-center justify-center gap-2">
                        <Badge 
                            variant={badge.earned ? "default" : "outline"} 
                            className={badge.earned ? tierStyle.text : ''}
                        >
                            {badge.tier?.toUpperCase()}
                        </Badge>
                        <span className="text-sm text-muted-foreground">+{badge.points} XP</span>
                    </div>
                    
                    {/* Earned date */}
                    {badge.earned && badge.earned_at && (
                        <p className="text-xs text-muted-foreground">
                            Earned {new Date(badge.earned_at).toLocaleDateString()}
                        </p>
                    )}
                </div>
            </motion.div>
        );
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Loader2 className="w-12 h-12 animate-spin text-primary" />
                </div>
            </MainLayout>
        );
    }

    const filteredBadges = getFilteredBadges();
    const earnedCount = badgeData?.earned_count || 0;
    const totalBadges = badgeData?.total_badges || 0;
    const completionPercent = totalBadges > 0 ? Math.round((earnedCount / totalBadges) * 100) : 0;

    return (
        <MainLayout>
            <div className="space-y-6" data-testid="badge-collection-page">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-bold font-heading flex items-center gap-3">
                            <Trophy className="h-8 w-8 text-yellow-500" />
                            Badge Collection
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Track your achievements and earn rewards for your progress
                        </p>
                    </div>
                    <Button variant="outline" onClick={fetchData} data-testid="refresh-badges-btn">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                </div>

                {/* Profile Summary */}
                <div className="grid md:grid-cols-4 gap-4">
                    {/* Level & XP */}
                    <Card className="md:col-span-2 bg-gradient-to-br from-primary/10 to-transparent border-primary/20">
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-6">
                                <div className="flex-shrink-0">
                                    <AnimatedScore 
                                        score={profileData?.level?.progress_percent || 0} 
                                        size="md" 
                                        label={`Level ${profileData?.level?.level || 1}`}
                                    />
                                </div>
                                <div className="flex-1 space-y-2">
                                    <h3 className="font-semibold text-lg">
                                        {profileData?.level?.title || 'Rookie Recruiter'}
                                    </h3>
                                    <XPBar
                                        currentXP={profileData?.total_points || 0}
                                        level={profileData?.level?.level || 1}
                                        xpToNextLevel={(profileData?.total_points || 0) + (profileData?.level?.points_to_next || 100)}
                                    />
                                    <p className="text-sm text-muted-foreground">
                                        {profileData?.level?.points_to_next || 0} XP to next level
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Badges Progress */}
                    <Card>
                        <CardContent className="pt-6 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-yellow-500/20 mb-3">
                                <Award className="w-8 h-8 text-yellow-500" />
                            </div>
                            <h3 className="text-2xl font-bold">{earnedCount}/{totalBadges}</h3>
                            <p className="text-sm text-muted-foreground">Badges Earned</p>
                            <Progress value={completionPercent} className="mt-3 h-2" />
                        </CardContent>
                    </Card>

                    {/* Streak */}
                    <Card>
                        <CardContent className="pt-6 text-center">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-orange-500/20 mb-3">
                                <Flame className="w-8 h-8 text-orange-500" />
                            </div>
                            <h3 className="text-2xl font-bold">{profileData?.streak?.current_streak || 0}</h3>
                            <p className="text-sm text-muted-foreground">Day Streak</p>
                            {profileData?.streak?.next_milestone && (
                                <p className="text-xs text-muted-foreground mt-2">
                                    {profileData.streak.next_milestone.remaining} days to next milestone
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Badge Categories */}
                <Card>
                    <CardHeader>
                        <CardTitle>Your Badges</CardTitle>
                        <CardDescription>
                            Complete challenges to unlock badges and earn XP
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="flex flex-wrap gap-2 h-auto bg-transparent mb-6">
                                <TabsTrigger 
                                    value="all" 
                                    className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                                >
                                    All ({totalBadges})
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="earned"
                                    className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white"
                                >
                                    <CheckCircle2 className="w-4 h-4 mr-1" />
                                    Earned ({earnedCount})
                                </TabsTrigger>
                                <TabsTrigger 
                                    value="locked"
                                    className="data-[state=active]:bg-slate-500 data-[state=active]:text-white"
                                >
                                    <Lock className="w-4 h-4 mr-1" />
                                    Locked ({totalBadges - earnedCount})
                                </TabsTrigger>
                                
                                {Object.entries(CATEGORY_CONFIG).map(([key, config]) => {
                                    const Icon = config.icon;
                                    const count = badgeData?.by_category?.[key]?.length || 0;
                                    if (count === 0) return null;
                                    
                                    return (
                                        <TabsTrigger 
                                            key={key}
                                            value={key}
                                            className="data-[state=active]:bg-primary/10"
                                        >
                                            <Icon className={`w-4 h-4 mr-1 ${config.color}`} />
                                            {config.label} ({count})
                                        </TabsTrigger>
                                    );
                                })}
                            </TabsList>

                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                                <AnimatePresence mode="popLayout">
                                    {filteredBadges.map(badge => renderBadgeCard(badge))}
                                </AnimatePresence>
                            </div>
                            
                            {filteredBadges.length === 0 && (
                                <div className="text-center py-12">
                                    <Gift className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                    <p className="text-muted-foreground">No badges in this category yet</p>
                                </div>
                            )}
                        </Tabs>
                    </CardContent>
                </Card>

                {/* Badge Detail Modal */}
                <AnimatePresence>
                    {selectedBadge && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
                            onClick={() => setSelectedBadge(null)}
                        >
                            <motion.div
                                initial={{ scale: 0.9, y: 20 }}
                                animate={{ scale: 1, y: 0 }}
                                exit={{ scale: 0.9, y: 20 }}
                                className="bg-card border rounded-2xl p-8 max-w-md w-full shadow-2xl"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {(() => {
                                    const Icon = BADGE_ICONS[selectedBadge.icon] || Award;
                                    const tierStyle = TIER_STYLES[selectedBadge.tier] || TIER_STYLES.bronze;
                                    
                                    return (
                                        <div className="text-center space-y-4">
                                            <div className={`
                                                w-24 h-24 mx-auto rounded-full flex items-center justify-center
                                                ${selectedBadge.earned 
                                                    ? `bg-gradient-to-br ${tierStyle.gradient} shadow-lg` 
                                                    : 'bg-muted'
                                                }
                                            `}>
                                                <Icon className={`w-12 h-12 ${selectedBadge.earned ? 'text-white' : 'text-muted-foreground'}`} />
                                            </div>
                                            
                                            <div>
                                                <h2 className="text-2xl font-bold">{selectedBadge.name}</h2>
                                                <p className="text-muted-foreground mt-2">{selectedBadge.description}</p>
                                            </div>
                                            
                                            <div className="flex items-center justify-center gap-3">
                                                <Badge className={selectedBadge.earned ? tierStyle.text : ''}>
                                                    {selectedBadge.tier?.toUpperCase()}
                                                </Badge>
                                                <span className="text-lg font-semibold">+{selectedBadge.points} XP</span>
                                            </div>
                                            
                                            {selectedBadge.earned ? (
                                                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                                                    <CheckCircle2 className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                                                    <p className="text-sm text-emerald-600 dark:text-emerald-400">
                                                        Earned on {new Date(selectedBadge.earned_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            ) : (
                                                <div className="p-4 rounded-lg bg-muted/50 border border-border">
                                                    <Lock className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
                                                    <p className="text-sm text-muted-foreground">
                                                        Keep practicing to unlock this badge!
                                                    </p>
                                                </div>
                                            )}
                                            
                                            <Button 
                                                variant="outline" 
                                                onClick={() => setSelectedBadge(null)}
                                                className="w-full"
                                            >
                                                Close
                                            </Button>
                                        </div>
                                    );
                                })()}
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </MainLayout>
    );
};

export default BadgeCollection;
